import { Capacitor } from "@capacitor/core";
import { env } from "@/lib/env";

/**
 * Native (Capacitor) sign-in helpers — audit H3 / M6.
 *
 * Every credential is env-driven (see docs/native-auth-setup.md); when one
 * is missing the matching button is hidden instead of failing at runtime.
 */

export const APP_ID = "fr.batchcooking.app";

export type AuthPlatform = "web" | "ios" | "android";

export function getAuthPlatform(): AuthPlatform {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : "web";
}

/**
 * Web uses Supabase's OAuth redirect (client IDs live in the Supabase
 * dashboard). Native needs the Google client IDs baked into the build:
 * Android requests an ID token for the **web** client ID, iOS signs in
 * with the iOS client ID and uses the web one as `serverClientId`.
 */
export function isGoogleSignInAvailable(): boolean {
  switch (getAuthPlatform()) {
    case "web":
      return true;
    case "android":
      return env.GOOGLE_WEB_CLIENT_ID.length > 0;
    case "ios":
      return (
        env.GOOGLE_IOS_CLIENT_ID.length > 0 &&
        env.GOOGLE_WEB_CLIENT_ID.length > 0
      );
  }
}

/** `@capacitor-community/apple-sign-in` has no Android implementation. */
export function isAppleSignInAvailable(): boolean {
  return getAuthPlatform() !== "android";
}

let googleInit: Promise<void> | null = null;

/** Idempotent `GoogleAuth.initialize()` — must run before `signIn()`. */
export function initGoogleAuth(): Promise<void> {
  if (!googleInit) {
    googleInit = (async () => {
      const { GoogleAuth } =
        await import("@codetrix-studio/capacitor-google-auth");
      await GoogleAuth.initialize({
        clientId:
          getAuthPlatform() === "ios"
            ? env.GOOGLE_IOS_CLIENT_ID
            : env.GOOGLE_WEB_CLIENT_ID,
        scopes: ["profile", "email"],
        grantOfflineAccess: false,
      });
    })().catch((err: unknown) => {
      googleInit = null;
      throw err;
    });
  }
  return googleInit;
}

/** Test hook — forget a previous initialisation. */
export function _resetGoogleInitForTests(): void {
  googleInit = null;
}

/** Native Google Sign-In → Google ID token for `signInWithIdToken`. */
export async function getGoogleIdTokenNative(): Promise<string> {
  if (!isGoogleSignInAvailable()) {
    throw new Error("Google Sign-In is not configured for this build.");
  }
  await initGoogleAuth();
  const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
  const user = await GoogleAuth.signIn();
  const idToken = user?.authentication?.idToken;
  if (!idToken) {
    throw new Error("No identity token received from Google.");
  }
  return idToken;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Random raw nonce + its SHA-256 (hex), as Apple expects the hashed one. */
export async function createNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = toHex(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(raw),
  );
  return { raw, hashed: toHex(new Uint8Array(digest)) };
}

/**
 * Native Sign in with Apple. Apple embeds the *hashed* nonce in the ID
 * token; Supabase receives the *raw* nonce and checks it (replay guard).
 */
export async function getAppleIdTokenNative(): Promise<{
  idToken: string;
  nonce: string;
}> {
  const { raw, hashed } = await createNonce();
  const { SignInWithApple } =
    await import("@capacitor-community/apple-sign-in");
  const result = await SignInWithApple.authorize({
    clientId: APP_ID,
    redirectURI: "",
    scopes: "email name",
    nonce: hashed,
  });
  const idToken = result?.response?.identityToken;
  if (!idToken) {
    throw new Error("No identity token received from Apple.");
  }
  return { idToken, nonce: raw };
}
